import { create } from 'zustand'
import { v4 as uuid } from 'uuid'
import {
  normalizePrepDashboardSettings,
  type PrepDashboardCard,
  type PrepDashboardSettings,
} from '../api/prep'
import {
  updateUserSettings,
  type LiveAdvanceMode,
  type PrepAgeMaxDays,
  type PrepDateBasis,
  type PrepLayout as PrepLayoutSetting,
  type ShowClockLiveTheme,
  type UserSettingsResponse,
} from '../api/userSettings'
import {
  getLocalDrawerFontScale,
  getLocalPrepLayout,
  getLocalPrepSingleLayoutSplit,
  getLocalPrepHeadlineLines,
  setLocalDrawerFontScale,
  setLocalPrepLayout,
  setLocalPrepSingleLayoutSplit,
  setLocalPrepHeadlineLines,
} from '../utils/storage'

export const DEFAULT_CHAT_LIST_PREVIEW_LINES = 2
export const DEFAULT_CLOCK_FUZZY = false

export const DEFAULT_LIVE_ADVANCE_MODE: LiveAdvanceMode = 'hybrid'

export const DEFAULT_PREP_DRAWER_FONT_SCALE = 1
export const DEFAULT_PREP_HEADLINE_LINES: 1 | 2 | 3 = 2
export const DEFAULT_PREP_AGE_MAX_DAYS: PrepAgeMaxDays = 0
export const DEFAULT_PREP_DATE_BASIS: PrepDateBasis = 'created_at'
export const DEFAULT_PREP_SEARCH_INCLUDE_DISMISSED = false
export const DEFAULT_PREP_LIST_SHOW_IMAGES = false
export const DEFAULT_PREP_LIST_SHOW_PREVIEW_TEXT = true
export const DEFAULT_SHOW_CLOCK_LIVE_PREP_TEASE = false
export const DEFAULT_SHOW_CLOCK_LIVE_HEADER_VISIBLE = true
export const DEFAULT_SHOW_CLOCK_LIVE_HEADER_FILL = true
export const DEFAULT_SHOW_CLOCK_LIVE_THEME: ShowClockLiveTheme = 'inherit'

export const PREP_AGE_MAX_DAY_OPTIONS: readonly PrepAgeMaxDays[] = [0, 1, 2, 3, 7]

export type PrepLayout = PrepLayoutSetting
export const DEFAULT_LAYOUT: PrepLayout = 'columns'
export const DEFAULT_SPLIT = 0.58
export const MIN_SPLIT = 0.3
export const MAX_SPLIT = 0.7

export function clampSplit(n: number): number {
  if (!Number.isFinite(n)) {
    return DEFAULT_SPLIT
  }
  const clamped = Math.max(MIN_SPLIT, Math.min(MAX_SPLIT, n))
  return Number(clamped.toFixed(2))
}

export const clampChatListPreviewLines = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.min(5, Math.max(0, Math.round(value)))
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed === '') {
      return DEFAULT_CHAT_LIST_PREVIEW_LINES
    }
    const parsed = Number.parseInt(trimmed, 10)
    if (Number.isFinite(parsed)) {
      return Math.min(5, Math.max(0, parsed))
    }
    return DEFAULT_CHAT_LIST_PREVIEW_LINES
  }

  if (typeof value === 'boolean') {
    return value ? 1 : 0
  }

  if (typeof value === 'object' && value !== null) {
    return DEFAULT_CHAT_LIST_PREVIEW_LINES
  }

  const numeric = Number(value)
  if (Number.isFinite(numeric)) {
    return Math.min(5, Math.max(0, Math.round(numeric)))
  }

  return DEFAULT_CHAT_LIST_PREVIEW_LINES
}

export const clampDrawerFontScale = (value: unknown): number => {
  const min = 0.85
  const max = 1.4
  const step = 0.05

  const toNumber = () => {
    if (typeof value === 'number') {
      return value
    }
    if (typeof value === 'string') {
      const parsed = Number.parseFloat(value)
      return Number.isFinite(parsed) ? parsed : Number.NaN
    }
    if (typeof value === 'boolean') {
      return value ? 1 : 0
    }
    if (value instanceof Number) {
      return value.valueOf()
    }
    return Number(value)
  }

  const numeric = toNumber()

  if (!Number.isFinite(numeric)) {
    return DEFAULT_PREP_DRAWER_FONT_SCALE
  }

  const clamped = Math.min(max, Math.max(min, numeric))
  const rounded = Math.round(clamped / step) * step
  return Number(rounded.toFixed(2))
}

const loadDrawerFontScale = (): number => {
  const stored = getLocalDrawerFontScale()
  if (typeof stored === 'number' && Number.isFinite(stored)) {
    return clampDrawerFontScale(stored)
  }
  return DEFAULT_PREP_DRAWER_FONT_SCALE
}

export const clampHeadlineLines = (value: unknown): 1 | 2 | 3 => {
  const numeric = (() => {
    if (typeof value === 'number') {
      return value
    }
    if (typeof value === 'string') {
      const parsed = Number.parseInt(value, 10)
      return Number.isFinite(parsed) ? parsed : Number.NaN
    }
    if (typeof value === 'boolean') {
      return value ? 1 : 0
    }
    if (value instanceof Number) {
      return value.valueOf()
    }
    return Number(value)
  })()

  if (!Number.isFinite(numeric)) {
    return DEFAULT_PREP_HEADLINE_LINES
  }

  const rounded = Math.round(numeric)
  const clamped = Math.min(3, Math.max(1, rounded)) as 1 | 2 | 3
  return clamped
}

const loadPrepHeadlineLines = (): 1 | 2 | 3 => {
  const stored = getLocalPrepHeadlineLines()
  if (stored !== null) {
    return clampHeadlineLines(stored)
  }
  return DEFAULT_PREP_HEADLINE_LINES
}

export const clampPrepAgeMaxDays = (value: unknown): PrepAgeMaxDays => {
  const numeric = typeof value === 'string' ? Number.parseInt(value, 10) : Number(value)
  if (!Number.isFinite(numeric)) {
    return DEFAULT_PREP_AGE_MAX_DAYS
  }
  const rounded = Math.floor(numeric)
  return (PREP_AGE_MAX_DAY_OPTIONS.find((option) => option === rounded) ?? DEFAULT_PREP_AGE_MAX_DAYS) as PrepAgeMaxDays
}

export const normalizePrepDateBasis = (value: unknown): PrepDateBasis =>
  value === 'published_at' ? 'published_at' : DEFAULT_PREP_DATE_BASIS

const DEFAULT_DASHBOARD_SETTINGS = normalizePrepDashboardSettings(undefined)

const loadPrepLayout = (): PrepLayout => {
  const stored = getLocalPrepLayout()
  if (stored === 'columns' || stored === 'single' || stored === 'dashboard') {
    return stored
  }
  return DEFAULT_LAYOUT
}

const loadSingleLayoutSplit = (): number => {
  const stored = getLocalPrepSingleLayoutSplit()
  if (typeof stored === 'number' && Number.isFinite(stored)) {
    return clampSplit(stored)
  }
  return DEFAULT_SPLIT
}

export const normalizeClockFuzzy = (value: unknown): boolean => {
  if (typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()

    if (normalized === '') {
      return DEFAULT_CLOCK_FUZZY
    }

    if (['1', 'true', 'yes', 'on'].includes(normalized)) {
      return true
    }

    if (['0', 'false', 'no', 'off'].includes(normalized)) {
      return false
    }
  }

  if (typeof value === 'number') {
    return value !== 0
  }

  if (value === null || value === undefined) {
    return DEFAULT_CLOCK_FUZZY
  }

  return Boolean(value)
}

export const normalizeLiveAdvanceMode = (value: unknown): LiveAdvanceMode => {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'auto' || normalized === 'hybrid' || normalized === 'manual') {
      return normalized
    }
    if (normalized === '') {
      return DEFAULT_LIVE_ADVANCE_MODE
    }
  }

  if (typeof value === 'boolean') {
    return value ? 'auto' : 'manual'
  }

  if (typeof value === 'number') {
    return value === 1 ? 'auto' : 'manual'
  }

  if (value === null || value === undefined) {
    return DEFAULT_LIVE_ADVANCE_MODE
  }

  return DEFAULT_LIVE_ADVANCE_MODE
}

type UserSettingsState = {
  chatListPreviewLines: number
  prep: {
    drawerFontScale: number
    layout: PrepLayout
    singleLayoutSplit: number
    headlineLines: 1 | 2 | 3
    dashboard: PrepDashboardSettings
    ageMaxDays: PrepAgeMaxDays
    dateBasis: PrepDateBasis
    searchIncludeDismissed: boolean
    listShowImages: boolean
    listShowPreviewText: boolean
  }
  showClock: {
    livePrepTease: boolean
    liveHeaderVisible: boolean
    liveHeaderFill: boolean
    liveTheme: ShowClockLiveTheme
  }
  clockFuzzy: boolean
  liveAdvanceMode: LiveAdvanceMode
  loaded: boolean
  setChatListPreviewLines: (value: number) => void
  setDrawerFontScale: (value: number) => void
  setLiveAdvanceMode: (value: LiveAdvanceMode) => void
  setPrepLayout: (layout: PrepLayout) => void
  setPrepHeadlineLines: (lines: 1 | 2 | 3) => void
  setSingleLayoutSplit: (split: number) => void
  setPrepDashboard: (dashboard: PrepDashboardSettings, options?: { persist?: boolean }) => void
  reorderDashboardCards: (movedId: string, targetIndex: number) => void
  addDashboardCategoryCards: (
    newCards: Array<{
      categoryId: number
      itemsPerPage: number
      collapsed?: boolean
      categoryName?: string | null
      categorySlug?: string | null
    }>,
  ) => Promise<void>
  setPrepAgeMaxDays: (days: PrepAgeMaxDays) => void
  setPrepDateBasis: (basis: PrepDateBasis) => void
  setPrepSearchIncludeDismissed: (flag: boolean) => void
  setListShowImages: (flag: boolean) => void
  setListShowPreviewText: (flag: boolean) => void
  setShowClockLivePrepTease: (flag: boolean) => void
  setShowClockLiveHeaderVisible: (flag: boolean) => void
  setShowClockLiveHeaderFill: (flag: boolean) => void
  setShowClockLiveTheme: (value: ShowClockLiveTheme) => void
  setClockFuzzy: (value: boolean) => void
  applyServerSettings: (settings: UserSettingsResponse) => void
  reset: () => void
}

const useUserSettingsStore = create<UserSettingsState>((set, get) => ({
  chatListPreviewLines: DEFAULT_CHAT_LIST_PREVIEW_LINES,
  prep: {
    drawerFontScale: loadDrawerFontScale(),
    layout: loadPrepLayout(),
    singleLayoutSplit: loadSingleLayoutSplit(),
    headlineLines: loadPrepHeadlineLines(),
    dashboard: DEFAULT_DASHBOARD_SETTINGS,
    ageMaxDays: DEFAULT_PREP_AGE_MAX_DAYS,
    dateBasis: DEFAULT_PREP_DATE_BASIS,
    searchIncludeDismissed: DEFAULT_PREP_SEARCH_INCLUDE_DISMISSED,
    listShowImages: DEFAULT_PREP_LIST_SHOW_IMAGES,
    listShowPreviewText: DEFAULT_PREP_LIST_SHOW_PREVIEW_TEXT,
  },
  showClock: {
    livePrepTease: DEFAULT_SHOW_CLOCK_LIVE_PREP_TEASE,
    liveHeaderVisible: DEFAULT_SHOW_CLOCK_LIVE_HEADER_VISIBLE,
    liveHeaderFill: DEFAULT_SHOW_CLOCK_LIVE_HEADER_FILL,
    liveTheme: DEFAULT_SHOW_CLOCK_LIVE_THEME,
  },
  clockFuzzy: DEFAULT_CLOCK_FUZZY,
  liveAdvanceMode: DEFAULT_LIVE_ADVANCE_MODE,
  loaded: false,
  setChatListPreviewLines: (value) =>
    set((state) => ({
      ...state,
      chatListPreviewLines: clampChatListPreviewLines(value),
      loaded: true,
    })),
  setDrawerFontScale: (value) => {
    const next = clampDrawerFontScale(value)
    set((state) => ({
      ...state,
      prep: {
        ...state.prep,
        drawerFontScale: next,
      },
      loaded: true,
    }))
    setLocalDrawerFontScale(next)
    scheduleDrawerFontScaleSave(next)
  },
  setPrepLayout: (layout) => {
    set((state) => {
      if (state.prep.layout === layout) {
        return { ...state, loaded: true }
      }
      return {
        ...state,
        prep: {
          ...state.prep,
          layout,
        },
        loaded: true,
      }
    })
    setLocalPrepLayout(layout)
    schedulePrepLayoutSave(layout)
  },
  setPrepHeadlineLines: (lines) => {
    const next = clampHeadlineLines(lines)
    set((state) => ({
      ...state,
      prep: {
        ...state.prep,
        headlineLines: next,
      },
      loaded: true,
    }))
    setLocalPrepHeadlineLines(next)
    schedulePrepHeadlineLinesSave(next)
  },
  setPrepAgeMaxDays: (days) => {
    const next = clampPrepAgeMaxDays(days)
    set((state) => {
      if (state.prep.ageMaxDays === next) {
        return state
      }
      return {
        ...state,
        prep: {
          ...state.prep,
          ageMaxDays: next,
        },
        loaded: true,
      }
    })
    schedulePrepAgeMaxDaysSave(next)
  },
  setPrepDateBasis: (basis) => {
    const next = normalizePrepDateBasis(basis)
    set((state) => {
      if (state.prep.dateBasis === next) {
        return state
      }
      return {
        ...state,
        prep: {
          ...state.prep,
          dateBasis: next,
        },
        loaded: true,
      }
    })
    schedulePrepDateBasisSave(next)
  },
  setPrepSearchIncludeDismissed: (flag) => {
    const next = Boolean(flag)
    set((state) => {
      if (state.prep.searchIncludeDismissed === next) {
        return state
      }
      return {
        ...state,
        prep: {
          ...state.prep,
          searchIncludeDismissed: next,
        },
        loaded: true,
      }
    })
    schedulePrepSearchIncludeDismissedSave(next)
  },
  setListShowImages: (flag) => {
    const next = Boolean(flag)
    set((state) => {
      if (state.prep.listShowImages === next) {
        return state
      }
      return {
        ...state,
        prep: {
          ...state.prep,
          listShowImages: next,
        },
        loaded: true,
      }
    })
    schedulePrepListShowImagesSave(next)
  },
  setListShowPreviewText: (flag) => {
    const next = Boolean(flag)
    set((state) => {
      if (state.prep.listShowPreviewText === next) {
        return state
      }
      return {
        ...state,
        prep: {
          ...state.prep,
          listShowPreviewText: next,
        },
        loaded: true,
      }
    })
    schedulePrepListShowPreviewTextSave(next)
  },
  setShowClockLivePrepTease: (flag) => {
    const next = Boolean(flag)
    set((state) => {
      if (state.showClock.livePrepTease === next) {
        return state
      }
      return {
        ...state,
        showClock: {
          ...state.showClock,
          livePrepTease: next,
        },
        loaded: true,
      }
    })
    scheduleShowClockLivePrepTeaseSave(next)
  },
  setShowClockLiveHeaderVisible: (flag) => {
    const next = Boolean(flag)
    set((state) => {
      if (state.showClock.liveHeaderVisible === next) {
        return state
      }
      return {
        ...state,
        showClock: {
          ...state.showClock,
          liveHeaderVisible: next,
        },
        loaded: true,
      }
    })
    scheduleShowClockLiveHeaderVisibleSave(next)
  },
  setShowClockLiveHeaderFill: (flag) => {
    const next = Boolean(flag)
    set((state) => {
      if (state.showClock.liveHeaderFill === next) {
        return state
      }
      return {
        ...state,
        showClock: {
          ...state.showClock,
          liveHeaderFill: next,
        },
        loaded: true,
      }
    })
    scheduleShowClockLiveHeaderFillSave(next)
  },
  setShowClockLiveTheme: (value) => {
    const next = value ?? DEFAULT_SHOW_CLOCK_LIVE_THEME
    set((state) => {
      if (state.showClock.liveTheme === next) {
        return state
      }
      return {
        ...state,
        showClock: {
          ...state.showClock,
          liveTheme: next,
        },
        loaded: true,
      }
    })
    scheduleShowClockLiveThemeSave(next)
  },
  setSingleLayoutSplit: (split) => {
    const next = clampSplit(split)
    set((state) => ({
      ...state,
      prep: {
        ...state.prep,
        singleLayoutSplit: next,
      },
      loaded: true,
    }))
    setLocalPrepSingleLayoutSplit(next)
    scheduleSingleLayoutSplitSave(next)
  },
  setPrepDashboard: (dashboard, options) => {
    set((state) => {
      if (state.prep.dashboard === dashboard) {
        return { ...state, loaded: true }
      }
      return {
        ...state,
        prep: {
          ...state.prep,
          dashboard,
        },
        loaded: true,
      }
    })

    if (options?.persist === false) {
      return
    }

    schedulePrepDashboardSave(dashboard)
  },
  reorderDashboardCards: (movedId, targetIndex) => {
    const {
      prep: { dashboard: currentDashboard },
      setPrepDashboard,
    } = get()

    const cards = currentDashboard.cards
    const currentIndex = cards.findIndex((card) => card.id === movedId)
    if (currentIndex === -1) {
      return
    }

    const sanitizedTargetIndex = (() => {
      if (!Number.isFinite(targetIndex)) {
        return currentIndex
      }
      const normalized = Math.floor(targetIndex)
      const maxIndex = Math.max(0, cards.length - 1)
      return Math.min(maxIndex, Math.max(0, normalized))
    })()

    if (sanitizedTargetIndex === currentIndex) {
      return
    }

    const nextCards = cards.slice()
    const [movedCard] = nextCards.splice(currentIndex, 1)
    if (!movedCard) {
      return
    }
    nextCards.splice(sanitizedTargetIndex, 0, movedCard)

    const nextDashboard: PrepDashboardSettings = {
      ...currentDashboard,
      cards: nextCards,
    }

    setPrepDashboard(nextDashboard)
  },
  addDashboardCategoryCards: async (incomingCards) => {
    const normalizeItemsPerPage = (value: number): number => {
      if (!Number.isFinite(value)) {
        return 5
      }
      const normalized = Math.floor(value)
      return Math.min(15, Math.max(3, normalized))
    }

    const uniqueSelection: Array<{
      categoryId: number
      itemsPerPage: number
      collapsed: boolean
      categoryName?: string
      categorySlug?: string
    }> = []
    const seenSelection = new Set<number>()

    for (const card of incomingCards) {
      const { categoryId } = card
      if (typeof categoryId !== 'number' || !Number.isFinite(categoryId)) {
        continue
      }
      if (seenSelection.has(categoryId)) {
        continue
      }
      seenSelection.add(categoryId)
      const rawName = typeof card.categoryName === 'string' ? card.categoryName : undefined
      const rawSlug = typeof card.categorySlug === 'string' ? card.categorySlug : undefined
      uniqueSelection.push({
        categoryId,
        itemsPerPage: normalizeItemsPerPage(card.itemsPerPage),
        collapsed: Boolean(card.collapsed),
        ...(rawName ? { categoryName: rawName.trim() } : {}),
        ...(rawSlug ? { categorySlug: rawSlug.trim() } : {}),
      })
    }

    if (uniqueSelection.length === 0) {
      return
    }

    const state = get()
    const previousDashboard = state.prep.dashboard
    const existingCategoryIds = new Set<number>()

    for (const card of previousDashboard.cards) {
      if (card.type === 'category' && typeof card.categoryId === 'number') {
        existingCategoryIds.add(card.categoryId)
      }
    }

    const cardsToAppend: PrepDashboardCard[] = []

    for (const card of uniqueSelection) {
      if (existingCategoryIds.has(card.categoryId)) {
        continue
      }
      existingCategoryIds.add(card.categoryId)
      cardsToAppend.push({
        id: uuid(),
        type: 'category',
        categoryId: card.categoryId,
        ...(card.categoryName ? { categoryName: card.categoryName } : {}),
        ...(card.categorySlug ? { categorySlug: card.categorySlug } : {}),
        itemsPerPage: card.itemsPerPage,
        collapsed: card.collapsed,
        page: 1,
      })
    }

    if (cardsToAppend.length === 0) {
      return
    }

    const nextDashboard: PrepDashboardSettings = {
      ...previousDashboard,
      cards: ensureMyPrepFirst([...previousDashboard.cards, ...cardsToAppend]),
    }

    set((current) => ({
      ...current,
      prep: {
        ...current.prep,
        dashboard: nextDashboard,
      },
      loaded: true,
    }))

    try {
      const response = await updateUserSettings({
        prepDashboard: serializePrepDashboard(nextDashboard),
      })

      if (response.prepDashboard) {
        const currentDashboard = useUserSettingsStore.getState().prep.dashboard
        const merged = mergeDashboardPages(response.prepDashboard, currentDashboard)
        useUserSettingsStore.getState().setPrepDashboard(merged, { persist: false })
      }
    } catch (error) {
      set((current) => ({
        ...current,
        prep: {
          ...current.prep,
          dashboard: previousDashboard,
        },
      }))
      throw error
    }
  },
  setClockFuzzy: (value) =>
    set((state) => ({
      ...state,
      clockFuzzy: Boolean(value),
      loaded: true,
    })),
  setLiveAdvanceMode: (value) => {
    const next = normalizeLiveAdvanceMode(value)
    set((state) => ({
      ...state,
      liveAdvanceMode: next,
      loaded: true,
    }))
    scheduleLiveAdvanceModeSave(next)
  },
  applyServerSettings: (settings) => {
    const nextLines = clampChatListPreviewLines(settings.chat_list_preview_lines)
    const serverScale = settings.prep?.drawerFontScale
    const serverLayout = settings.prep?.layout
    const serverSplit = settings.prep?.singleLayoutSplit
    const serverHeadlineLines = settings.prep?.headlineLines
    const serverDashboard = settings.prepDashboard
    const serverAgeMaxDays = settings.prep?.ageMaxDays
    const serverDateBasis = settings.prep?.dateBasis
    const serverSearchIncludeDismissed = settings.prep?.searchIncludeDismissed
    const serverListShowImages = settings.prep?.listShowImages
    const serverListShowPreviewText = settings.prep?.listShowPreviewText
    const serverLiveAdvanceMode = settings.liveAdvanceMode
    const serverShowClockLivePrepTease = settings.showClock?.livePrepTease
    const serverShowClockLiveHeaderVisible = settings.showClock?.liveHeaderVisible
    const serverShowClockLiveHeaderFill = settings.showClock?.liveHeaderFill
    const serverShowClockLiveTheme = settings.showClock?.liveTheme
    const nextScale =
      serverScale !== undefined ? clampDrawerFontScale(serverScale) : loadDrawerFontScale()
    const nextLayout =
      serverLayout === 'single' || serverLayout === 'columns' || serverLayout === 'dashboard'
        ? serverLayout
        : loadPrepLayout()
    const nextSplit =
      serverSplit !== undefined ? clampSplit(serverSplit) : loadSingleLayoutSplit()
    const nextHeadlineLines =
      serverHeadlineLines !== undefined
        ? clampHeadlineLines(serverHeadlineLines)
        : loadPrepHeadlineLines()
    const nextDashboard =
      serverDashboard !== undefined
        ? normalizePrepDashboardSettings(serverDashboard)
        : DEFAULT_DASHBOARD_SETTINGS
    const nextAgeMaxDays =
      serverAgeMaxDays !== undefined
        ? clampPrepAgeMaxDays(serverAgeMaxDays)
        : DEFAULT_PREP_AGE_MAX_DAYS
    const nextDateBasis = normalizePrepDateBasis(serverDateBasis)
    const nextSearchIncludeDismissed =
      serverSearchIncludeDismissed !== undefined
        ? Boolean(serverSearchIncludeDismissed)
        : DEFAULT_PREP_SEARCH_INCLUDE_DISMISSED
    const nextListShowImages =
      serverListShowImages !== undefined
        ? Boolean(serverListShowImages)
        : DEFAULT_PREP_LIST_SHOW_IMAGES
    const nextListShowPreviewText =
      serverListShowPreviewText !== undefined
        ? Boolean(serverListShowPreviewText)
        : DEFAULT_PREP_LIST_SHOW_PREVIEW_TEXT
    const nextShowClockLivePrepTease =
      serverShowClockLivePrepTease !== undefined
        ? Boolean(serverShowClockLivePrepTease)
        : DEFAULT_SHOW_CLOCK_LIVE_PREP_TEASE
    const nextShowClockLiveHeaderVisible =
      serverShowClockLiveHeaderVisible !== undefined
        ? Boolean(serverShowClockLiveHeaderVisible)
        : DEFAULT_SHOW_CLOCK_LIVE_HEADER_VISIBLE
    const nextShowClockLiveHeaderFill =
      serverShowClockLiveHeaderFill !== undefined
        ? Boolean(serverShowClockLiveHeaderFill)
        : DEFAULT_SHOW_CLOCK_LIVE_HEADER_FILL
    const nextShowClockLiveTheme = serverShowClockLiveTheme ?? DEFAULT_SHOW_CLOCK_LIVE_THEME

    if (serverScale !== undefined) {
      setLocalDrawerFontScale(nextScale)
    }

    if (serverLayout !== undefined) {
      setLocalPrepLayout(nextLayout)
    }

    if (serverSplit !== undefined) {
      setLocalPrepSingleLayoutSplit(nextSplit)
    }

    if (serverHeadlineLines !== undefined) {
      setLocalPrepHeadlineLines(nextHeadlineLines)
    }

    const nextLiveAdvanceMode = normalizeLiveAdvanceMode(serverLiveAdvanceMode)

    set({
      chatListPreviewLines: nextLines,
      prep: {
        drawerFontScale: nextScale,
        layout: nextLayout,
        singleLayoutSplit: nextSplit,
        headlineLines: nextHeadlineLines,
        dashboard: nextDashboard,
        ageMaxDays: nextAgeMaxDays,
        dateBasis: nextDateBasis,
        searchIncludeDismissed: nextSearchIncludeDismissed,
        listShowImages: nextListShowImages,
        listShowPreviewText: nextListShowPreviewText,
      },
      showClock: {
        livePrepTease: nextShowClockLivePrepTease,
        liveHeaderVisible: nextShowClockLiveHeaderVisible,
        liveHeaderFill: nextShowClockLiveHeaderFill,
        liveTheme: nextShowClockLiveTheme,
      },
      clockFuzzy: normalizeClockFuzzy(settings.clock_fuzzy),
      liveAdvanceMode: nextLiveAdvanceMode,
      loaded: true,
    })
  },
  reset: () =>
    set({
      chatListPreviewLines: DEFAULT_CHAT_LIST_PREVIEW_LINES,
      prep: {
        drawerFontScale: loadDrawerFontScale(),
        layout: loadPrepLayout(),
        singleLayoutSplit: loadSingleLayoutSplit(),
        headlineLines: loadPrepHeadlineLines(),
        dashboard: DEFAULT_DASHBOARD_SETTINGS,
        ageMaxDays: DEFAULT_PREP_AGE_MAX_DAYS,
        dateBasis: DEFAULT_PREP_DATE_BASIS,
        searchIncludeDismissed: DEFAULT_PREP_SEARCH_INCLUDE_DISMISSED,
        listShowImages: DEFAULT_PREP_LIST_SHOW_IMAGES,
        listShowPreviewText: DEFAULT_PREP_LIST_SHOW_PREVIEW_TEXT,
      },
      showClock: {
        livePrepTease: DEFAULT_SHOW_CLOCK_LIVE_PREP_TEASE,
        liveHeaderVisible: DEFAULT_SHOW_CLOCK_LIVE_HEADER_VISIBLE,
        liveHeaderFill: DEFAULT_SHOW_CLOCK_LIVE_HEADER_FILL,
        liveTheme: DEFAULT_SHOW_CLOCK_LIVE_THEME,
      },
      clockFuzzy: DEFAULT_CLOCK_FUZZY,
      liveAdvanceMode: DEFAULT_LIVE_ADVANCE_MODE,
      loaded: false,
    }),
}))

let drawerFontScaleSaveTimeout: ReturnType<typeof setTimeout> | null = null
let prepLayoutSaveTimeout: ReturnType<typeof setTimeout> | null = null
let singleLayoutSplitSaveTimeout: ReturnType<typeof setTimeout> | null = null
let prepDashboardSaveTimeout: ReturnType<typeof setTimeout> | null = null
let prepHeadlineLinesSaveTimeout: ReturnType<typeof setTimeout> | null = null
let prepAgeMaxDaysSaveTimeout: ReturnType<typeof setTimeout> | null = null
let prepDateBasisSaveTimeout: ReturnType<typeof setTimeout> | null = null
let prepSearchIncludeDismissedSaveTimeout: ReturnType<typeof setTimeout> | null = null
let prepListShowImagesSaveTimeout: ReturnType<typeof setTimeout> | null = null
let prepListShowPreviewTextSaveTimeout: ReturnType<typeof setTimeout> | null = null
let liveAdvanceModeSaveTimeout: ReturnType<typeof setTimeout> | null = null
let showClockLivePrepTeaseSaveTimeout: ReturnType<typeof setTimeout> | null = null
let showClockLiveHeaderVisibleSaveTimeout: ReturnType<typeof setTimeout> | null = null
let showClockLiveHeaderFillSaveTimeout: ReturnType<typeof setTimeout> | null = null
let showClockLiveThemeSaveTimeout: ReturnType<typeof setTimeout> | null = null

const scheduleDrawerFontScaleSave = (value: number) => {
  if (drawerFontScaleSaveTimeout) {
    clearTimeout(drawerFontScaleSaveTimeout)
  }

  drawerFontScaleSaveTimeout = setTimeout(async () => {
    drawerFontScaleSaveTimeout = null
    try {
      const response = await updateUserSettings({ prep: { drawerFontScale: value } })
      useUserSettingsStore.getState().applyServerSettings(response)
    } catch {
      // ignore – local fallback will retain the user's choice
    }
  }, 400)
}

const schedulePrepLayoutSave = (layout: PrepLayout) => {
  if (prepLayoutSaveTimeout) {
    clearTimeout(prepLayoutSaveTimeout)
  }

  prepLayoutSaveTimeout = setTimeout(async () => {
    prepLayoutSaveTimeout = null
    try {
      const response = await updateUserSettings({ prep: { layout } })
      useUserSettingsStore.getState().applyServerSettings(response)
    } catch {
      // ignore errors, local state persists
    }
  }, 200)
}

const scheduleSingleLayoutSplitSave = (split: number) => {
  if (singleLayoutSplitSaveTimeout) {
    clearTimeout(singleLayoutSplitSaveTimeout)
  }

  singleLayoutSplitSaveTimeout = setTimeout(async () => {
    singleLayoutSplitSaveTimeout = null
    try {
      const response = await updateUserSettings({ prep: { singleLayoutSplit: split } })
      useUserSettingsStore.getState().applyServerSettings(response)
    } catch {
      // ignore errors, local state persists
    }
  }, 250)
}

const schedulePrepHeadlineLinesSave = (lines: 1 | 2 | 3) => {
  if (prepHeadlineLinesSaveTimeout) {
    clearTimeout(prepHeadlineLinesSaveTimeout)
  }

  prepHeadlineLinesSaveTimeout = setTimeout(async () => {
    prepHeadlineLinesSaveTimeout = null
    try {
      const response = await updateUserSettings({ prep: { headlineLines: lines } })
      useUserSettingsStore.getState().applyServerSettings(response)
    } catch {
      // ignore errors, local state persists
    }
  }, 200)
}

const schedulePrepAgeMaxDaysSave = (days: PrepAgeMaxDays) => {
  if (prepAgeMaxDaysSaveTimeout) {
    clearTimeout(prepAgeMaxDaysSaveTimeout)
  }

  prepAgeMaxDaysSaveTimeout = setTimeout(async () => {
    prepAgeMaxDaysSaveTimeout = null
    try {
      const response = await updateUserSettings({ prep: { ageMaxDays: days } })
      useUserSettingsStore.getState().applyServerSettings(response)
    } catch {
      // ignore errors – local optimistic state will remain until the next save attempt
    }
  }, 200)
}

const schedulePrepDateBasisSave = (basis: PrepDateBasis) => {
  if (prepDateBasisSaveTimeout) {
    clearTimeout(prepDateBasisSaveTimeout)
  }

  prepDateBasisSaveTimeout = setTimeout(async () => {
    prepDateBasisSaveTimeout = null
    try {
      const response = await updateUserSettings({ prep: { dateBasis: basis } })
      useUserSettingsStore.getState().applyServerSettings(response)
    } catch {
      // Keep the optimistic local selection until a later settings refresh.
    }
  }, 200)
}

const schedulePrepSearchIncludeDismissedSave = (flag: boolean) => {
  if (prepSearchIncludeDismissedSaveTimeout) {
    clearTimeout(prepSearchIncludeDismissedSaveTimeout)
  }

  prepSearchIncludeDismissedSaveTimeout = setTimeout(async () => {
    prepSearchIncludeDismissedSaveTimeout = null
    try {
      const response = await updateUserSettings({ prep: { searchIncludeDismissed: flag } })
      useUserSettingsStore.getState().applyServerSettings(response)
    } catch {
      // ignore errors, optimistic UI keeps the current toggle state
    }
  }, 200)
}

const schedulePrepListShowImagesSave = (flag: boolean) => {
  if (prepListShowImagesSaveTimeout) {
    clearTimeout(prepListShowImagesSaveTimeout)
  }

  prepListShowImagesSaveTimeout = setTimeout(async () => {
    prepListShowImagesSaveTimeout = null
    try {
      const response = await updateUserSettings({ prep: { listShowImages: flag } })
      useUserSettingsStore.getState().applyServerSettings(response)
    } catch {
      // ignore errors, optimistic state persists until the next save attempt
    }
  }, 200)
}

const schedulePrepListShowPreviewTextSave = (flag: boolean) => {
  if (prepListShowPreviewTextSaveTimeout) {
    clearTimeout(prepListShowPreviewTextSaveTimeout)
  }

  prepListShowPreviewTextSaveTimeout = setTimeout(async () => {
    prepListShowPreviewTextSaveTimeout = null
    try {
      const response = await updateUserSettings({ prep: { listShowPreviewText: flag } })
      useUserSettingsStore.getState().applyServerSettings(response)
    } catch {
      // ignore errors, optimistic state persists until the next save attempt
    }
  }, 200)
}

const scheduleLiveAdvanceModeSave = (mode: LiveAdvanceMode) => {
  if (liveAdvanceModeSaveTimeout) {
    clearTimeout(liveAdvanceModeSaveTimeout)
  }

  liveAdvanceModeSaveTimeout = setTimeout(async () => {
    liveAdvanceModeSaveTimeout = null
    try {
      const response = await updateUserSettings({ liveAdvanceMode: mode })
      useUserSettingsStore.getState().applyServerSettings(response)
    } catch {
      // ignore errors, local state will remain
    }
  }, 300)
}

const scheduleShowClockLivePrepTeaseSave = (flag: boolean) => {
  if (showClockLivePrepTeaseSaveTimeout) {
    clearTimeout(showClockLivePrepTeaseSaveTimeout)
  }

  showClockLivePrepTeaseSaveTimeout = setTimeout(async () => {
    showClockLivePrepTeaseSaveTimeout = null
    try {
      const response = await updateUserSettings({
        showClock: { livePrepTease: flag },
      })
      useUserSettingsStore.getState().applyServerSettings(response)
    } catch {
      // ignore errors, optimistic state persists
    }
  }, 250)
}

const scheduleShowClockLiveHeaderVisibleSave = (flag: boolean) => {
  if (showClockLiveHeaderVisibleSaveTimeout) {
    clearTimeout(showClockLiveHeaderVisibleSaveTimeout)
  }

  showClockLiveHeaderVisibleSaveTimeout = setTimeout(async () => {
    showClockLiveHeaderVisibleSaveTimeout = null
    try {
      const response = await updateUserSettings({
        showClock: { liveHeaderVisible: flag },
      })
      useUserSettingsStore.getState().applyServerSettings(response)
    } catch {
      // ignore errors, optimistic state persists
    }
  }, 250)
}

const scheduleShowClockLiveHeaderFillSave = (flag: boolean) => {
  if (showClockLiveHeaderFillSaveTimeout) {
    clearTimeout(showClockLiveHeaderFillSaveTimeout)
  }

  showClockLiveHeaderFillSaveTimeout = setTimeout(async () => {
    showClockLiveHeaderFillSaveTimeout = null
    try {
      const response = await updateUserSettings({
        showClock: { liveHeaderFill: flag },
      })
      useUserSettingsStore.getState().applyServerSettings(response)
    } catch {
      // ignore errors, optimistic state persists
    }
  }, 250)
}

const scheduleShowClockLiveThemeSave = (value: ShowClockLiveTheme) => {
  if (showClockLiveThemeSaveTimeout) {
    clearTimeout(showClockLiveThemeSaveTimeout)
  }

  showClockLiveThemeSaveTimeout = setTimeout(async () => {
    showClockLiveThemeSaveTimeout = null
    try {
      const response = await updateUserSettings({
        showClock: { liveTheme: value },
      })
      useUserSettingsStore.getState().applyServerSettings(response)
    } catch {
      // ignore errors, optimistic state persists
    }
  }, 250)
}

const mergeDashboardPages = (
  incoming: PrepDashboardSettings,
  current: PrepDashboardSettings,
): PrepDashboardSettings => {
  const pageMap = new Map<string, number>()
  for (const card of current.cards) {
    if (typeof card.page === 'number' && Number.isFinite(card.page)) {
      pageMap.set(card.id, Math.max(1, Math.floor(card.page)))
    }
  }

  let hasChanges = false
  const mergedCards = incoming.cards.map((card) => {
    const retainedPage = pageMap.get(card.id)
    if (retainedPage !== undefined && retainedPage !== card.page) {
      hasChanges = true
      return { ...card, page: retainedPage }
    }
    return card
  })

  if (!hasChanges) {
    return incoming
  }

  // Preserve local pagination while respecting any other server-driven updates.
  return { ...incoming, cards: mergedCards }
}

const schedulePrepDashboardSave = (dashboard: PrepDashboardSettings) => {
  if (prepDashboardSaveTimeout) {
    clearTimeout(prepDashboardSaveTimeout)
  }

  prepDashboardSaveTimeout = setTimeout(async () => {
    prepDashboardSaveTimeout = null
    try {
      const response = await updateUserSettings({
        prepDashboard: serializePrepDashboard(dashboard),
      })

      if (response.prepDashboard) {
        const currentDashboard = useUserSettingsStore.getState().prep.dashboard
        const merged = mergeDashboardPages(response.prepDashboard, currentDashboard)
        useUserSettingsStore.getState().setPrepDashboard(merged, { persist: false })
      }
    } catch {
      // ignore – local state will remain until the next successful save
    }
  }, 500)
}

const ensureMyPrepFirst = (cards: PrepDashboardCard[]): PrepDashboardCard[] => {
  const myPrepCard = cards.find((card) => card.type === 'my_prep')

  if (myPrepCard) {
    return cards
  }

  return [
    {
      id: uuid(),
      type: 'my_prep',
      itemsPerPage: 5,
      collapsed: false,
      page: 1,
    },
    ...cards,
  ]
}

const serializePrepDashboard = (dashboard: PrepDashboardSettings) => ({
  enabled: dashboard.enabled,
  columns: dashboard.columns,
  cards: dashboard.cards.map((card) => {
    const base = {
      id: card.id,
      type: card.type,
      itemsPerPage: card.itemsPerPage,
      collapsed: card.collapsed,
      ...(card.headlineLines ? { headlineLines: card.headlineLines } : {}),
      ...(card.listShowImages !== undefined ? { listShowImages: card.listShowImages } : {}),
      ...(card.column ? { column: card.column } : {}),
      ...(card.columnOrder ? { columnOrder: card.columnOrder } : {}),
      // Page is ephemeral UI state and intentionally omitted from persistence.
    }

    if (card.type === 'category' && typeof card.categoryId === 'number') {
      const trimmedName = card.categoryName?.trim() ?? ''
      const trimmedSlug = card.categorySlug?.trim() ?? ''
      return {
        ...base,
        categoryId: card.categoryId,
        ...(trimmedSlug ? { categorySlug: trimmedSlug } : {}),
        ...(trimmedName ? { categoryName: trimmedName } : {}),
      }
    }

    return base
  }),
})

export default useUserSettingsStore
